import { useCallback, useEffect, useMemo, useRef } from 'react';

import { PAGE_TYPES, Page } from '../enums';
import { useSelectedItemsStore } from '../store';
import type { SelectedItemsStore } from '../store/Inbox/SelectedItems/types';
import { useUserCommunicationsStore } from '../store/Inbox/UserCommunications';
import { isLocalDraftSelectionId } from '../utils/localDraftSelection';

type SelectedItemsKey = keyof SelectedItemsStore['selectedItems'];
type SelectedItemsSetter = Extract<keyof SelectedItemsStore, `set${string}`>;

const NO_SELECTION: string[] = [];

const PAGE_SELECTION: Partial<
  Record<Page, [SelectedItemsKey, SelectedItemsSetter]>
> = {
  [PAGE_TYPES.INBOX]: ['inboxItems', 'setInboxItems'],
  [PAGE_TYPES.SENT]: ['sentItems', 'setSentItems'],
  [PAGE_TYPES.DRAFT]: ['draftItems', 'setDraftItems'],
  [PAGE_TYPES.SPAM]: ['spamItems', 'setSpamItems'],
  [PAGE_TYPES.TRASH]: ['trashItems', 'setTrashItems'],
  [PAGE_TYPES.ALL]: ['allMailItems', 'setAllMailItems'],
  [PAGE_TYPES.LABELS]: ['labelsItems', 'setLabelsItems'],
};

function getPageSelectionAccessors(page: Page) {
  const entry = PAGE_SELECTION[page];
  const state = useSelectedItemsStore.getState();
  return {
    get: () => (entry ? state.selectedItems[entry[0]] : NO_SELECTION),
    set: (items: string[]) => entry?.[1] && state[entry[1]](items),
  };
}

function readRenderedSelection(
  page: Page | undefined,
  renderedIds: Set<string>,
) {
  if (!page) return new Set<string>();
  return new Set(
    getPageSelectionAccessors(page)
      .get()
      .filter((id) => renderedIds.has(id)),
  );
}

export function useToggleItemSelectionPersistent<
  T extends { messageId: string },
>(items: T[] = [], page?: Page) {
  const selectionKey = page ? PAGE_SELECTION[page]?.[0] : undefined;
  const stored = useSelectedItemsStore((s) =>
    selectionKey ? s.selectedItems[selectionKey] : NO_SELECTION,
  );

  const renderedIds = useMemo(
    () => new Set(items.map((item) => item.messageId)),
    [items],
  );
  const renderedIdsRef = useRef(renderedIds);
  renderedIdsRef.current = renderedIds;

  const selectedItemsZustand = useMemo(
    () => new Set(stored.filter((id) => renderedIds.has(id))),
    [stored, renderedIds],
  );

  const saveSelection = useCallback(
    (newSelection: Set<string>) => {
      if (!page) return;
      getPageSelectionAccessors(page).set(Array.from(newSelection));
    },
    [page],
  );

  const toggleItemSelection = useCallback(
    (messageId: string) => {
      const newSelection = readRenderedSelection(page, renderedIdsRef.current);
      if (newSelection.has(messageId)) {
        newSelection.delete(messageId);
      } else {
        newSelection.add(messageId);
      }
      saveSelection(newSelection);
    },
    [page, saveSelection],
  );

  const toggleSelectAll = useCallback(() => {
    if (!page) return;

    const renderedNow = renderedIdsRef.current;
    const current = readRenderedSelection(page, renderedNow);
    if (current.size === renderedNow.size) {
      useSelectedItemsStore.getState().clearSelection();
    } else {
      saveSelection(new Set(renderedNow));
    }
  }, [page, saveSelection]);

  const clearSelectionPage = useCallback(() => {
    useSelectedItemsStore.getState().clearSelection();
  }, []);

  useEffect(() => {
    if (!page) return;

    const unsubscribe = useUserCommunicationsStore.subscribe((state) => {
      const getStoreMessageIds = () => {
        switch (page) {
          case PAGE_TYPES.INBOX:
            return new Set([
              ...state.primary.map((msg) => msg.messageId),
              ...state.promotions.map((msg) => msg.messageId),
              ...state.social.map((msg) => msg.messageId),
              ...state.updates.map((msg) => msg.messageId),
              ...state.forums.map((msg) => msg.messageId),
            ]);
          case PAGE_TYPES.SENT:
            return new Set(state.sent.map((msg) => msg.messageId));
          case PAGE_TYPES.DRAFT:
            return new Set(state.draft.map((msg) => msg.messageId));
          case PAGE_TYPES.SPAM:
            return new Set(state.spam.map((msg) => msg.messageId));
          case PAGE_TYPES.TRASH:
            return new Set(state.trash.map((msg) => msg.messageId));
          case PAGE_TYPES.ALL:
            return new Set(state.all.map((msg) => msg.messageId));
          case PAGE_TYPES.LABELS:
            return new Set(
              Object.values(state.labels)
                .flat()
                .map((msg) => msg.messageId),
            );
          default:
            return new Set();
        }
      };

      const storeMessageIds = getStoreMessageIds();
      const accessors = getPageSelectionAccessors(page);
      const currentSelectedItems = accessors.get();

      const isStale = (messageId: string) =>
        !isLocalDraftSelectionId(messageId) && !storeMessageIds.has(messageId);
      const itemsToRemove = currentSelectedItems.filter(isStale);

      if (itemsToRemove.length > 0) {
        const newSelection = currentSelectedItems.filter(
          (messageId) => !isStale(messageId),
        );
        accessors.set(newSelection);
      }
    });

    return unsubscribe;
  }, [page]);

  const isAllSelected =
    items.length > 0 && selectedItemsZustand.size === items.length;
  const hasSelectedItems = selectedItemsZustand.size > 0;

  return {
    selectedItems: selectedItemsZustand,
    toggleItemSelection,
    toggleSelectAll,
    clearSelection: clearSelectionPage,
    isAllSelected,
    hasSelectedItems,
  };
}
